import { router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "@/lib/i18n"
import { isStandalone } from "@/lib/standalone"
import { cn } from "@/lib/utils"

// Pull distance (in the indicator's damped pixels) past which releasing fires a
// refresh. Below it, releasing cancels with no reload.
const THRESHOLD = 70
// The indicator only travels half as far as the finger, and never wanders more
// than this off the top of the screen.
const DAMPING = 0.5
const MAX_PULL = 120

// How far the indicator has moved for a given raw finger travel. Non-downward
// gestures (delta <= 0) yield 0 so the gesture never arms on an upward swipe.
// Exported pure so the arming maths can be reviewed and unit-tested in isolation.
export function dampedPull(rawDelta: number): number {
  if (rawDelta <= 0) {
    return 0
  }
  return Math.min(rawDelta * DAMPING, MAX_PULL)
}

export function isPastThreshold(pull: number): boolean {
  return pull >= THRESHOLD
}

// The gesture only arms at the very top of the page; anywhere else the browser
// should scroll normally and never trigger a refresh.
export function atTop(): boolean {
  return (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0
}

// Custom pull-to-refresh for the installed PWA only. In a normal browser tab the
// native gesture already reloads, so this stays inert there (see issue #75).
export function PullToRefresh() {
  const { t } = useTranslation()
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const pullRef = useRef(0)
  const refreshingRef = useRef(false)
  const startY = useRef<number | null>(null)

  useEffect(() => {
    if (!isStandalone()) {
      return
    }
    const scrollRoot = document.documentElement

    function reset() {
      pullRef.current = 0
      refreshingRef.current = false
      startY.current = null
      scrollRoot.style.overscrollBehaviorY = ""
      setPull(0)
      setRefreshing(false)
    }

    function onStart(event: TouchEvent) {
      // Only single-finger gestures that begin at the very top may arm.
      startY.current = event.touches.length === 1 && atTop() ? event.touches[0].clientY : null
    }

    function onMove(event: TouchEvent) {
      if (startY.current === null || refreshingRef.current) {
        return
      }
      const distance = dampedPull(event.touches[0].clientY - startY.current)
      if (distance > 0) {
        // We own the gesture now — stop the page from rubber-banding under it.
        scrollRoot.style.overscrollBehaviorY = "contain"
        pullRef.current = distance
        setPull(distance)
      }
    }

    function onEnd() {
      if (startY.current === null || refreshingRef.current) {
        return
      }
      startY.current = null
      if (isPastThreshold(pullRef.current)) {
        refreshingRef.current = true
        pullRef.current = THRESHOLD
        setRefreshing(true)
        setPull(THRESHOLD)
        router.reload({ onFinish: reset })
      } else {
        reset()
      }
    }

    window.addEventListener("touchstart", onStart, { passive: true })
    window.addEventListener("touchmove", onMove, { passive: true })
    window.addEventListener("touchend", onEnd, { passive: true })
    window.addEventListener("touchcancel", onEnd, { passive: true })
    return () => {
      window.removeEventListener("touchstart", onStart)
      window.removeEventListener("touchmove", onMove)
      window.removeEventListener("touchend", onEnd)
      window.removeEventListener("touchcancel", onEnd)
    }
  }, [])

  if (pull === 0 && !refreshing) {
    return null
  }

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center"
      style={{ transform: `translateY(${pull}px)` }}
    >
      <span
        role="status"
        aria-label={t("pull_to_refresh.aria_label")}
        className={cn(
          "mt-2 flex h-10 w-10 items-center justify-center rounded-full border shadow-lg transition-colors",
          isPastThreshold(pull)
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-card text-muted-foreground",
        )}
      >
        <Loader2 className={cn("h-5 w-5", refreshing && "animate-spin")} />
      </span>
    </div>
  )
}
